import { ErrorResponse } from 'react-router-dom';
import {
  IDamageManagementDataSource,
  IDamageManagementInitialValues,
  IParentResponse,
  IResponse,
  ISummaryTableItem,
} from '../types';
import { prepareFormData } from '../utils/functions';
import { IUseHttpServiceProps } from '../services/type';
import { RequestMethods } from '../services/enum';
import { IExpandedRow } from '../components/DataTable/type';

export default class DamageManagementService {
  private readonly baseUrl = 'damage-management/damage-management/';

  constructor(
    private readonly executeService: <T>(
      args: IUseHttpServiceProps,
    ) => Promise<T | ErrorResponse | null>,
    private readonly parameters: string,
  ) {}

  async fetchData(): Promise<
    IResponse<IDamageManagementDataSource> | ErrorResponse | null
  > {
    return await this.executeService<IResponse<IDamageManagementDataSource>>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${this.parameters}`,
    });
  }

  async fetchItemById(
    id: number,
  ): Promise<IDamageManagementDataSource | ErrorResponse | null> {
    return await this.executeService<IDamageManagementDataSource>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async deleteItemById(id: number): Promise<unknown | ErrorResponse | null> {
    return await this.executeService<unknown>({
      method: RequestMethods.DELETE,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async exportData(): Promise<{ download_url: string } | ErrorResponse | null> {
    return await this.executeService<{ download_url: string }>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}export-excel/${this.parameters}`,
      contentType: 'multipart/form-data',
    });
  }

  async submitForm(
    values: IDamageManagementInitialValues,
    selectedItem?: IDamageManagementDataSource,
  ): Promise<IDamageManagementDataSource | ErrorResponse | null> {
    const formData = prepareFormData<IDamageManagementInitialValues>(values);

    const method = selectedItem ? RequestMethods.PATCH : RequestMethods.POST;
    const url = selectedItem
      ? `${this.baseUrl}${selectedItem.id}/`
      : this.baseUrl;

    return await this.executeService<IDamageManagementDataSource>({
      method,
      url,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async uploadFiles(
    id: number,
    files: FileList,
  ): Promise<IDamageManagementDataSource | ErrorResponse | null> {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('upload_files', file);
    });

    return await this.executeService<IDamageManagementDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/upload-files/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async deleteFile(
    id: number,
    fileId: number,
  ): Promise<IDamageManagementDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('file_id', fileId.toString());

    return await this.executeService<IDamageManagementDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/delete-file/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async fetchExpandedRows(
    id: number,
  ): Promise<IExpandedRow[] | ErrorResponse | null> {
    return await this.executeService<IExpandedRow[]>({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/get-latest-processes/`,
    });
  }

  async fetchSummaryTable(
    id: string,
  ): Promise<ISummaryTableItem[] | ErrorResponse | null> {
    return await this.executeService<ISummaryTableItem[]>({
      method: RequestMethods.GET,
      url: `damage-management/summary-table/${id}/get-summary-table/`,
    });
  }

  async bindFiles(
    rowIds: number[],
    organization?: number,
  ): Promise<IDamageManagementDataSource | ErrorResponse | null> {
    return await this.executeService<IDamageManagementDataSource>({
      method: RequestMethods.POST,
      url: `damage-management/parent/`,
      data: {
        damage_managements: rowIds,
        organization,
      },
    });
  }

  async fetchParent(
    id: number,
  ): Promise<IParentResponse | ErrorResponse | null> {
    return await this.executeService<IParentResponse>({
      method: RequestMethods.GET,
      url: `damage-management/parent/${id}/`,
    });
  }

  async removeParent(
    id: number,
    payload: IDamageManagementDataSource,
  ): Promise<unknown | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.PATCH,
      url: `${this.baseUrl}${id}/`,
      data: { ...payload, parent: null },
    });
  }

  async updateStatus(
    id: number,
    status: string,
    emailUsers: number[],
  ): Promise<IDamageManagementDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('status', status);
    formData.append('email_users', emailUsers.join(','));

    return await this.executeService<IDamageManagementDataSource>({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/update-status/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async cancel(id: number): Promise<unknown | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('id', id.toString());
    formData.append('status', 'cancel_expected');

    return await this.executeService({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/update-status/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }
}
